/**
 * TraineeFilterContext — shared, persisted Department → Batch filter for the
 * whole Trainee Management module.
 *
 * Cascading contract:
 *   Department → Batch → page data
 *   - Selecting a department narrows the Batch dropdown to that department.
 *   - Department = "All" shows org-wide data; a specific department (batch=All)
 *     scopes data to every batch in that department.
 *   - Selection persists across tab switches (sessionStorage).
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export const ALL = '__all__';
const DEPT_KEY = 'lumyn_tm_department';
const BATCH_KEY = 'lumyn_tm_batch';

const Ctx = createContext(null);

export function TraineeFilterProvider({ children }) {
  const { get } = useApi();
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [departmentId, setDepartmentIdState] = useState(
    () => sessionStorage.getItem(DEPT_KEY) || ALL,
  );
  const [batchId, setBatchIdState] = useState(
    () => sessionStorage.getItem(BATCH_KEY) || ALL,
  );

  const refresh = useCallback(() => {
    get('/trainees/departments?include_inactive=true')
      .then((r) => setDepartments(r.departments || [])).catch(() => setDepartments([]));
    get('/trainees/batches')
      .then((r) => setBatches(r.batches || [])).catch(() => setBatches([]));
  }, [get]);
  useEffect(() => { refresh(); }, [refresh]);

  // Batches visible for the current department selection (cascading).
  const visibleBatches = useMemo(() => {
    if (departmentId === ALL) return batches;
    return batches.filter((b) => b.department_id === departmentId);
  }, [batches, departmentId]);

  const setDepartmentId = useCallback((next) => {
    setDepartmentIdState(next);
    sessionStorage.setItem(DEPT_KEY, next);
    // Cascade: if the current batch no longer belongs to the new department, reset it.
    setBatchIdState((prevBatch) => {
      if (prevBatch === ALL) return prevBatch;
      const stillValid = next === ALL
        || batches.some((b) => b.batch_id === prevBatch && b.department_id === next);
      const resolved = stillValid ? prevBatch : ALL;
      sessionStorage.setItem(BATCH_KEY, resolved);
      return resolved;
    });
  }, [batches]);

  const setBatchId = useCallback((next) => {
    setBatchIdState(next);
    sessionStorage.setItem(BATCH_KEY, next);
  }, []);

  // Query fragment (no leading ?/&) for the active scope.
  const scopeQS = useCallback(() => {
    if (batchId !== ALL) return `batch_id=${encodeURIComponent(batchId)}`;
    if (departmentId !== ALL) return `department_id=${encodeURIComponent(departmentId)}`;
    return '';
  }, [batchId, departmentId]);

  const value = useMemo(() => ({
    departments, batches, visibleBatches,
    departmentId, batchId, setDepartmentId, setBatchId, scopeQS, refresh,
  }), [departments, batches, visibleBatches, departmentId, batchId, setDepartmentId, setBatchId, scopeQS, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTraineeFilter() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTraineeFilter must be used within TraineeFilterProvider');
  return ctx;
}

/** Shared Department ▼ Batch ▼ bar. `extra` renders after the batch select. */
export function TraineeFilterBar({ extra = null, className = '' }) {
  const { departments, visibleBatches, departmentId, batchId, setDepartmentId, setBatchId } = useTraineeFilter();
  return (
    <div className={`flex flex-wrap items-end gap-3 ${className}`} data-testid="tm-filter-bar">
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">Department</label>
        <Select value={departmentId} onValueChange={setDepartmentId}>
          <SelectTrigger className="w-56" data-testid="tm-department-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">Batch</label>
        <Select value={batchId} onValueChange={setBatchId}>
          <SelectTrigger className="w-56" data-testid="tm-batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All batches</SelectItem>
            {visibleBatches.map((b) => (
              <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {extra}
    </div>
  );
}
